import React, { useEffect, useState } from 'react';
import Layout from './Layout';
import PageHeader from './PageHeader';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Pagination,
  FormControl,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAllProducts, fetchAllProductVariants, createProduct, createProductVariant, restockProductVariant } from '../api';

const PRODUCTS_PER_PAGE = 5;

const ProductPage = () => {
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showProductVariantModal, setShowProductVariantModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [newProduct, setNewProduct] = useState({ name: '', description: '' });
  const [newVariant, setNewVariant] = useState({
    product: '',
    size: '',
    color: '',
    buying_price: '',
    selling_price: '',
    stock: ''
  });
  const [restockModal, setRestockModal] = useState({
    visible: false,
    variant: null,
    quantity: 0,
    tag: '',
    vendor_name: ''
  });

  // Fetch all products (all pages)
  const fetchAllProductsList = async () => {
    setLoading(true);
    try {
      const all = await fetchAllProducts();
      setAllProducts(all);
      setProducts(all.slice(0, PRODUCTS_PER_PAGE));
    } catch (error) {
      console.error('Error fetching all products:', error);
    }
    setLoading(false);
  };

  // Fetch all product variants using centralized API service
  const fetchVariants = async () => {
    setLoading(true);
    try {
      const allVariants = await fetchAllProductVariants();
      setVariants(allVariants);
    } catch (error) {
      console.error('Error fetching variants:', error);
    }
    setLoading(false);
  };

  // Filter products and variants based on search term (all data)
  const filteredProducts = allProducts.filter((product) => {
    if (product.name.toLowerCase().includes(searchTerm.toLowerCase())) return true;
    return variants.some(
      (variant) =>
        variant.product === product.id &&
        ([variant.size, variant.color]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  });

  // Pagination logic (frontend)
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleProductInput = (e) => {
    setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
  };

  const handleVariantInput = (e) => {
    setNewVariant({ ...newVariant, [e.target.name]: e.target.value });
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      await createProduct(newProduct);
      setMessage('Product added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setNewProduct({ name: '', description: '' });
      fetchAllProductsList();
    } catch (error) {
      setMessage('Failed to add product.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error:', error);
    }
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();
    try {
      await createProductVariant(newVariant);
      setMessage('Variant added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setNewVariant({
        product: '',
        size: '',
        color: '',
        buying_price: '',
        selling_price: '',
        stock: ''
      });
      fetchVariants();
    } catch (error) {
      setMessage('Failed to add variant.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error:', error);
    }
  };

  const handleRestockInput = (e) => {
    setRestockModal({ ...restockModal, [e.target.name]: e.target.value });
  };

  const handleRestock = async (e) => {
    e.preventDefault();
    try {
      await restockProductVariant({
        product_variant: restockModal.variant.id,
        quantity: restockModal.quantity,
        tag: restockModal.tag,
        vendor_name: restockModal.vendor_name
      });
      setMessage('Variant restocked successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setRestockModal({ ...restockModal, visible: false, quantity: 0 });
      fetchVariants();
    } catch (error) {
      setMessage('Failed to restock variant!');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error:', error);
    }
  };

  const toggleRestockModal = (variant = null) => {
    setRestockModal({
      visible: !restockModal.visible,
      variant,
      quantity: 0,
      tag: '',
      vendor_name: ''
    });
  };

  useEffect(() => {
    fetchAllProductsList();
    fetchVariants();
  }, []);

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 'lg', mx: 'auto' }}>
        {/* Page Header - subtle, uniform, and space-efficient */}
        <PageHeader title="Products" subtitle="Manage your products and variants." />

        {/* Search Input */}
        <Box mb={4} display="flex" justifyContent="center">
          <TextField
            label="Search products or variants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            sx={{ width: { xs: '100%', sm: '60%' }, maxWidth: 500 }}
          />
        </Box>

        {/* Add Product/Variant Buttons */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={4} justifyContent="center" alignItems="center">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowProductModal(true)} sx={{ minWidth: 180 }}>
            Add Product
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowProductVariantModal(true)} sx={{ minWidth: 220 }}>
            Add Product Variant
          </Button>
        </Stack>

        {/* Product Modal */}
        <Dialog open={showProductModal} onClose={() => setShowProductModal(false)}>
          <DialogTitle>
            Add a New Product
            <IconButton
              aria-label="close"
              onClick={() => setShowProductModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleAddProduct} sx={{ mt: 2 }}>
              <TextField
                label="Name"
                name="name"
                value={newProduct.name}
                onChange={handleProductInput}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              <TextField
                label="Description"
                name="description"
                value={newProduct.description}
                onChange={handleProductInput}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              <DialogActions>
                <Button onClick={() => setShowProductModal(false)}>Cancel</Button>
                <Button type="submit" variant="contained">Add Product</Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Product Variant Modal */}
        <Dialog open={showProductVariantModal} onClose={() => setShowProductVariantModal(false)}>
          <DialogTitle>
            Add a Product Variant
            <IconButton
              aria-label="close"
              onClick={() => setShowProductVariantModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleAddVariant} sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Autocomplete
                  options={allProducts}
                  getOptionLabel={(option) => option.name || ''}
                  value={allProducts.find(p => p.id === newVariant.product) || null}
                  onChange={(event, newValue) => {
                    setNewVariant({ ...newVariant, product: newValue ? newValue.id : '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Product" required />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </FormControl>
              {['size', 'color', 'buying_price', 'selling_price', 'stock'].map((field) => (
                <TextField
                  key={field}
                  label={field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  name={field}
                  value={newVariant[field]}
                  onChange={handleVariantInput}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
              ))}
              <DialogActions>
                <Button onClick={() => setShowProductVariantModal(false)}>Cancel</Button>
                <Button type="submit" variant="contained">Add Variant</Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Restock Modal */}
        <Dialog open={restockModal.visible} onClose={() => toggleRestockModal()}>
          <DialogTitle>
            Restock
            <IconButton
              aria-label="close"
              onClick={() => toggleRestockModal()}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{restockModal.variant?.product_name}</Typography>
            <Typography variant="body2">📏 {restockModal.variant?.size}</Typography>
            <Typography variant="body2">🔴🟢🔵 {restockModal.variant?.color}</Typography>
            <Box component="form" onSubmit={handleRestock} sx={{ mt: 2 }}>
              {['quantity', 'tag', 'vendor_name'].map((field) => (
                <TextField
                  key={field}
                  label={field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  name={field}
                  value={restockModal[field]}
                  onChange={handleRestockInput}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                />
              ))}
              <DialogActions>
                <Button onClick={() => toggleRestockModal()}>Cancel</Button>
                <Button type="submit" variant="contained">Restock</Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Product List with Variants */}
        <Stack spacing={3}>
          {paginatedProducts.length === 0 ? (
            <Typography align="center" color="text.secondary">No products found.</Typography>
          ) : (
            paginatedProducts.map((product) => (
              <Card key={product.id} variant="outlined" sx={{ borderRadius: 3, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>{product.name}</Typography>
                  <List>
                    {variants
                      .filter(
                        (v) =>
                          v.product === product.id &&
                          (searchTerm === "" ||
                            v.size?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            v.color?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      )
                      .map((variant, idx, arr) => (
                        <ListItem
                          key={variant.id}
                          alignItems="flex-start"
                          sx={{
                            mb: idx !== arr.length - 1 ? 2 : 0,
                            borderRadius: 2,
                            background: '#f9fbfd',
                            boxShadow: '0 1px 4px 0 rgba(60,60,60,0.04)',
                            border: '1px solid #e3eafc',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: 1,
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Box flex={1} minWidth={0}>
                            <ListItemText
                              primary={
                                <span>
                                  📏 {variant.size} &nbsp;|&nbsp; 🔴🟢🔵 {variant.color} &nbsp;|&nbsp; 🗄️ {variant.stock} Pcs
                                </span>
                              }
                            />
                          </Box>
                          <Box sx={{ mt: { xs: 1, sm: 0 }, minWidth: 120, display: 'flex', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
                            <Button variant="outlined" size="small" onClick={() => toggleRestockModal(variant)}>
                              Restock
                            </Button>
                          </Box>
                        </ListItem>
                      ))}
                  </List>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        {/* Pagination Controls (frontend) */}
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>

        {/* Snackbar for feedback */}
        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default ProductPage;
